/**
 * EditAccountModal Component
 * Modal for editing financial account name and institution
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import { budgetApi } from '../../api/endpoints/budget';
import type { FinancialAccount } from '../../api/types';
import logger from '../../utils/logger';

interface EditAccountModalProps {
  visible: boolean;
  onClose: () => void;
  account: FinancialAccount | null;
  onSaved: () => void;
}

export default function EditAccountModal({ visible, onClose, account, onSaved }: EditAccountModalProps) {
  const [name, setName] = useState('');
  const [institutionName, setInstitutionName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (account && visible) {
      setName(account.name);
      setInstitutionName(account.institution_name || '');
    }
  }, [account, visible]);

  const handleClose = () => {
    setName('');
    setInstitutionName('');
    onClose();
  };

  const handleSave = async () => {
    if (!account) return;
    if (!name.trim()) {
      Alert.alert('Erreur', 'Le nom du compte est obligatoire');
      return;
    }

    setIsSubmitting(true);
    try {
      await budgetApi.updateFinancialAccount(account.id, {
        name: name.trim(),
      });
      onSaved();
      handleClose();
    } catch (err: any) {
      logger.error('[EditAccountModal] Save error:', err);
      Alert.alert('Erreur', 'Impossible de mettre à jour le compte');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={handleClose}>
      <View style={styles.overlay}>
        <View style={styles.content}>
          <View style={styles.header}>
            <Text style={styles.title}>Modifier le compte</Text>
            <TouchableOpacity onPress={handleClose}>
              <Icon name="close" size={24} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>

          <Text style={styles.label}>Nom du compte</Text>
          <TextInput
            style={styles.input}
            value={name}
            onChangeText={setName}
            placeholder="Ex: Compte Courant"
            placeholderTextColor={colors.textSecondary}
          />

          {institutionName ? (
            <>
              <Text style={styles.label}>Banque / Institution</Text>
              <Text style={styles.readOnlyValue}>{institutionName}</Text>
            </>
          ) : null}

          <View style={styles.actions}>
            <TouchableOpacity style={[styles.button, styles.cancelButton]} onPress={handleClose}>
              <Text style={styles.cancelButtonText}>Annuler</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.submitButton, isSubmitting && styles.submitButtonDisabled]}
              onPress={handleSave}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.submitButtonText}>Enregistrer</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    backgroundColor: colors.background,
    borderRadius: 16,
    padding: spacing.lg,
    width: '90%',
    maxWidth: 440,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.text,
  },
  label: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  input: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    fontSize: typography.fontSize.md,
    color: colors.text,
    marginBottom: spacing.md,
  },
  readOnlyValue: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.md,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  button: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
  },
  cancelButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
  },
  submitButton: {
    backgroundColor: colors.primary,
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  submitButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: '#FFFFFF',
  },
});
